import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, CircularProgress, Alert, Box } from '@mui/material';
import { stripePromise } from '@/util/stripe';
import { useManualStripePaymentIntent } from '@/queries/payments/useManualStripePaymentIntent';
import { getBuyPanelsPath } from '../route';

interface ManualStripePaymentFormProps {
    requestUuid: string;
    token?: string;
    paymentMethod?: string;
}

const CheckoutForm = ({ requestUuid, token }: { requestUuid: string; token?: string }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const routePrefix = getBuyPanelsPath(window.location.pathname, '');

    const buildSuccessUrl = () => {
        const params = new URLSearchParams();
        params.set('requestId', requestUuid);
        if (token) {
            params.set('token', token);
        }
        return `${window.location.origin}${routePrefix}/manual-payment/success?${params.toString()}`;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: buildSuccessUrl(),
            },
            redirect: 'if_required',
        });

        if (error) {
            setErrorMessage(error.message || 'An unexpected error occurred.');
            setIsLoading(false);
            return;
        }

        if (paymentIntent?.id) {
            const params = new URLSearchParams();
            params.set('requestId', requestUuid);
            params.set('payment_intent', paymentIntent.id);
            if (token) {
                params.set('token', token);
            }
            window.location.assign(`${routePrefix}/manual-payment/success?${params.toString()}`);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
                <PaymentElement />
            </Box>

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={!stripe || isLoading}
                    fullWidth
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isLoading ? 'Processing...' : 'Pay Now'}
                </Button>
            </Box>
        </form>
    );
};

export default function ManualStripePaymentForm({ requestUuid, token, paymentMethod }: ManualStripePaymentFormProps) {
    const { clientSecret, error, loading } = useManualStripePaymentIntent(requestUuid, token, paymentMethod);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (loading || !clientSecret) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                appearance: {
                    theme: 'stripe',
                },
            }}
        >
            <div className="space-y-4 py-2">
                <CheckoutForm requestUuid={requestUuid} token={token} />
            </div>
        </Elements>
    );
}
